/**
 * Splitting a league's rows into seasons, newest first.
 *
 * GitHub issue #31: a player's last five matches and a team's results and
 * fixtures should show the season being played now, with earlier ones behind
 * tabs. Both screens need the same two answers -- which seasons are here, and
 * which is the current one -- so they ask one function rather than two.
 *
 * ORDERED BY DATE, NOT BY NAME. Season names are words: "Autumn 2026" sorts
 * before "Spring 2026" alphabetically and after it in life, so sorting the
 * names would file last winter's matches as the current season every year the
 * league runs. The newest row in each season is what says which season is
 * newest, and the rows already carry the fixture's own play date.
 *
 * A row whose season is empty -- a native league that never opened one -- is
 * kept under `null` rather than dropped. It is somebody's match, and a screen
 * that silently omitted it would be lying about how many they played.
 */
export function bySeason(rows, dateOf = (r) => r.played_on) {
  const groups = new Map();
  for (const row of rows || []) {
    const key = row.season || null;
    if (!groups.has(key)) groups.set(key, { season: key, rows: [], newest: '' });
    const g = groups.get(key);
    g.rows.push(row);
    const when = dateOf(row) || '';
    if (when > g.newest) g.newest = when;
  }
  // Descending by each season's own newest row. `newest` is an ISO date
  // string, so a plain string comparison IS a date comparison -- the one
  // place where sorting text is the right thing here.
  return [...groups.values()].sort((a, b) => (a.newest < b.newest ? 1 : -1));
}

/**
 * The label for a season tab. A season with no name is somebody's matches all
 * the same, and "—" would read as a missing value rather than as a pile you
 * can open.
 */
export const seasonLabel = (s) => s || 'Unfiled';

/**
 * A season name as a `data-testid` fragment.
 *
 * Season names hold spaces -- "Spring 2026" -- and a space in a testid makes
 * `[data-testid=...]` an invalid CSS selector rather than one that simply
 * fails to match, so a test cannot even ask for the element. Slugged the same
 * way player.js already slugs a season for its honours and previous-season
 * rows, rather than a second convention for the same problem.
 */
export const seasonId = (s) => (s || 'unfiled').replace(/\s+/g, '-');
